import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, Chip, Stack, Skeleton,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Gavel as GavelIcon } from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { formatCurrency, formatDate } from '@/utils/format';
import { disciplinaryApi } from '@/api/endpoints/hr';

interface ConductTabProps {
  employeeId: string;
  currencyCode: string;
}

export default function ConductTab({ employeeId, currencyCode }: ConductTabProps) {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['discipline-summary', employeeId],
    queryFn: () => disciplinaryApi.getEmployeeSummary(employeeId).then(r => r.data),
    enabled: !!employeeId,
  });

  if (isLoading) return <Skeleton variant="rounded" height={200} />;
  if (!summary || summary.total_cases === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <GavelIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.semibold }}>No Conduct Records</Typography>
          <Typography sx={{ fontSize: TYPE.base, color: 'text.secondary' }}>
            You have a clean disciplinary record.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 2 }}>Conduct Summary</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography sx={{ fontSize: TYPE['2xl'], fontWeight: TYPE.bold }}>{summary.total_cases}</Typography>
              <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>Total Cases</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography sx={{ fontSize: TYPE['2xl'], fontWeight: TYPE.bold, color: summary.active_cases > 0 ? 'warning.main' : 'text.primary' }}>{summary.active_cases}</Typography>
              <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>Active</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography sx={{ fontSize: TYPE['2xl'], fontWeight: TYPE.bold }}>{summary.warnings_count}</Typography>
              <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>Warnings</Typography>
            </Grid>
            {summary.fines_total > 0 && (
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography sx={{ fontSize: TYPE['2xl'], fontWeight: TYPE.bold, color: 'error.main' }}>{formatCurrency(summary.fines_total, currencyCode)}</Typography>
                <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>Fines</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {summary.cases.map((c: any) => (
        <Card key={c.id}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>{c.case_number}</Typography>
              <Stack direction="row" spacing={1}>
                <Chip label={c.severity} size="small" color={c.severity === 'minor' ? 'info' : c.severity === 'moderate' ? 'warning' : 'error'} />
                <Chip label={c.status} size="small" variant="outlined" />
              </Stack>
            </Box>
            <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary', mb: 0.5 }}>
              Incident: {formatDate(c.incident_date)} {c.violation_category_name ? `· ${c.violation_category_name}` : ''}
            </Typography>
            {c.outcomes?.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {c.outcomes.map((o: any) => (
                  <Chip
                    key={o.id}
                    label={o.outcome_type.replace(/_/g, ' ')}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5, textTransform: 'capitalize' }}
                    color={o.outcome_type.includes('warning') ? 'warning' : o.outcome_type === 'suspension' ? 'error' : 'default'}
                  />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}
